import { lruMemoize } from '../../../util/lruMemoize';
import { cleanEmail } from '../../email/sanitize/cleanEmail';
import { evaluateTextAsDom } from './evaluateTextAsDom';

/*
  We go bottom up from the current node to see if we should render its content.
  Only inline styles are checked (element.style), not computed/class-based styles —
  this is intentional since sanitization strips <style> tags, leaving only inline styles.

  1. `display: none` hides a node unconditionally at any ancestor level
  2. `visibility: hidden` hides a node, but can be overridden by a descendant's
     `visibility: visible` — once we find `visibility: visible` walking up, we stop
     checking visibility (see note below), but we continue walking to catch any
     `display: none` ancestor above that point
*/
const getNodeIsVisible = (node, container) => {
  let element = node.parentElement;
  let visibilityFound = false;
  while (element && element !== container) {
    if (element.style.display === 'none') {
      return false;
    }

    /*
      The element lowest in any ancestor path with `visibility` determines the visibility for
      it and all of its descendants. If `visibility: hidden`, we return false immediately. If
      `visibility: visible`, we skip further visibility checks but keep walking to catch any
      `display: none` ancestor above.
    */
    if (!visibilityFound) {
      if (element.style.visibility === 'hidden') {
        return false;
      } else if (element.style.visibility === 'visible') {
        visibilityFound = true;
      }
    }
    element = element.parentElement;
  }
  return true;
};
const concatVisibleText = walker => {
  let node = walker.nextNode();
  let text = '';
  while (node) {
    text += node.textContent;
    node = walker.nextNode();
  }
  return text.trim();
};

/*
  When an element is in a not in the main `document` DOM tree, it's `.innerText` will return all
  text, including that which has `display: none` or `visibility: hidden` on it which an element in
  `document.body` would not return. We use a tree walker to search through all parts of the DOM
  without needing to boilerplate how to handle the DOM ourselves.
*/
const getVisibleText = container => {
  const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return getNodeIsVisible(node, container) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  return concatVisibleText(walker);
};

/**
 * @description Returns the visible text content of a pre-built DOM container.
 * Use `getEvaluatedTextContent` instead when starting from an HTML string.
 */
export const getEvaluatedTextContentDom = container => document.contains(container) ? container.innerText.trim() : getVisibleText(container);

/**
 * @description Given a raw HTML string, returns the text content computed by
 * rendering it in the DOM. Use `getEvaluatedTextContentDom` directly when you already
 * have a container.
 */
export const getEvaluatedTextContent = lruMemoize(html => evaluateTextAsDom(cleanEmail(html), getEvaluatedTextContentDom, document));